import type {RumInitConfiguration} from '@datadog/browser-rum';

import {SESSION_PROVIDER_TAG} from '#lit/components/providers/session_provider/index.js';
import {sessionStore} from '#stores/session/index.js';
import type {NodeEnvironment} from '#types/environment.js';
import {getCurrentEnv} from '#utils/environment.js';

const RUM_SERVICE = 'vortex-frontend';
const RUM_APP_ID = '7574dd34-c9a9-490d-b41b-07ce1834629a';
const RUM_CLIENT_TOKEN = 'pubb657b0315bba69e1c469dd94c3049186';
const CURRENT_ENV = getCurrentEnv();

export const ENV_SPECIFIC_CONFIGS: Record<
  NodeEnvironment,
  Partial<RumInitConfiguration>
> = {
  development: {
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: false,
  },

  production: {
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
  },
};

const DEFAULT_CONFIG: RumInitConfiguration = {
  site: 'datadoghq.com',
  applicationId: RUM_APP_ID,
  clientToken: RUM_CLIENT_TOKEN,
  service: RUM_SERVICE,
  env: window.GL_TENANT,
  version: window.GL_DD_VERSION,
  defaultPrivacyLevel: 'mask-user-input',
  compressIntakeRequests: true,
  ...ENV_SPECIFIC_CONFIGS[CURRENT_ENV],
};

const getRum = async () => {
  try {
    const {datadogRum} = await import('@datadog/browser-rum');
    return datadogRum ?? null;
  } catch {
    return null;
  }
};

function getAllowedTracingUrls(): RumInitConfiguration['allowedTracingUrls'] {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return undefined;
  }
  return [window.location.origin];
}

export const initDataDog = async (
  config: Partial<RumInitConfiguration> = {}
) => {
  try {
    const rum = await getRum();
    const enabled = CURRENT_ENV === 'production' || process.env.LOCAL_DD;

    if (rum && enabled) {
      const tracingUrls = getAllowedTracingUrls();
      rum.init({
        ...DEFAULT_CONFIG,
        ...(tracingUrls ? {allowedTracingUrls: tracingUrls} : {}),
        ...config,
      });
    }
  } catch (error) {
    console.error('Error initializing DataDog', error);
  }
};

export const logDatadogError = async (
  error: Error,
  customAttributes?: Record<string, unknown>
): Promise<void> => {
  try {
    const rum = await getRum();
    if (rum) {
      rum.addError(error, customAttributes);
    }
  } catch {
    // Swallow so an unhandled rejection never triggers onUnhandledRejection → logError → loop
  }
};

let didAddRumContextFromSession = false;

const setRumContextFromSession = (rum: Awaited<ReturnType<typeof getRum>>) => {
  if (!rum) return;

  const {org, team, user} = sessionStore.getState();
  if (!org) return;

  const set = (key: string, value: unknown) =>
    rum.setGlobalContextProperty(key, value);

  set('organizationId', org.id);
  set('organizationName', org.name);
  if (team !== null) {
    set('teamId', team.id);
    set('teamName', team.name);
  }
  if (user !== null) {
    set('userId', user.id);
    rum.setUser({id: String(user.id)});
  } else {
    rum.clearUser();
  }
};

const addRumContextFromSessionOnce = async () => {
  if (didAddRumContextFromSession) return;
  didAddRumContextFromSession = true;

  const rum = await getRum();
  setRumContextFromSession(rum);
};

export const clearDatadogRumUser = async (): Promise<void> => {
  try {
    const rum = await getRum();
    rum?.clearUser();
  } catch {
    // Swallow so an unhandled rejection never triggers onUnhandledRejection → logError → loop
  }
};

export const addRumContextFromSessionWhenReady = () => {
  if (Object.keys(sessionStore.getState().flags).length > 0) {
    addRumContextFromSessionOnce();
    return;
  }

  document.querySelector(SESSION_PROVIDER_TAG)?.addEventListener(
    'rendered',
    () => {
      addRumContextFromSessionOnce();
    },
    {once: true}
  );
};
